<template>
  <div class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray">
    <div class="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
      <div class="text-center">
        <LogoMainLink class="mx-auto" />
        <div v-if="error" class="mt-8">
          <div class="rounded-full w-16 h-16 bg-red-100 mx-auto flex items-center justify-center">
            <svg class="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 class="mt-4 text-xl font-semibold text-gray-900">Authentication Failed</h2>
          <p class="mt-2 text-sm text-gray-600">{{ error }}</p>
          <router-link to="/login" class="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cosmicPurple hover:bg-cosmicPurple/90 transition-colors">Try again</router-link>
        </div>
        <div v-else class="mt-8">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-cosmicPurple border-t-transparent mx-auto"></div>
          <h2 class="mt-4 text-xl font-semibold text-gray-900">Processing authentication...</h2>
          <p class="mt-2 text-sm text-gray-600">Please wait while we complete your sign in.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import type { SocialAuthCallbackParams } from '@/types/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const error = ref<string | null>(null)

onMounted(async () => {
  const params = route.query as unknown as SocialAuthCallbackParams
  if (params.error) { error.value = getErrorMessage(params.error); return }
  if (!params.access_token) { error.value = 'No access token received. Please try again.'; return }

  try {
    await authStore.setTokens({ accessToken: params.access_token, refreshToken: params.refresh_token || '' })
    const isProfileComplete = params.is_profile_complete === 'true'
    await new Promise(resolve => setTimeout(resolve, 200))

    if (!isProfileComplete) {
      await router.push('/complete-profile').catch(() => { window.location.href = '/complete-profile' })
    } else {
      const redirectTo = localStorage.getItem('redirectAfterLogin')
      if (redirectTo) { localStorage.removeItem('redirectAfterLogin'); await router.push(redirectTo).catch(() => { window.location.href = redirectTo }) }
      else { await router.push('/').catch(() => { window.location.href = '/' }) }
    }
  } catch (err: any) { error.value = err.message || 'Failed to complete authentication. Please try again.' }
})

function getErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    access_denied: 'You denied access to your account. Please try again if you want to continue.',
    invalid_request: 'The authentication request was invalid. Please try again.',
    server_error: 'A server error occurred. Please try again later.',
    temporarily_unavailable: 'The authentication service is temporarily unavailable. Please try again later.'
  }
  return errorMessages[errorCode] || `Authentication error: ${errorCode}`
}
</script>
